import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useRelayEnvironment } from 'react-relay';

import logger from 'src/util/logger';
import {
  calculateTradeSetSummaryBatchSize,
  generateBatchTimeRanges,
} from 'src/util/tradeSetSummary/batchCalculator';

import {
  getTotalRulesCount,
} from '../helpers/dataBatching';
import {
  getPropertyDataFromCache,
  initialiseCache,
} from '../helpers/dataCaching';
import { PropertyShowLoadingActions } from '../reducer/constants';

/**
 * Custom hook that encapsulates all property data fetching.
 *
 * Handles:
 * - Batched data loading with progress tracking via reducer dispatch
 * - Data transformation from GraphQL response to meter objects
 * - Error handling and user feedback
 *
 * Caching is managed by the `dataCaching` module (module-level singleton).
 * This hook delegates to `getPropertyDataFromCache`, passing `propertyId`
 * so the cache layer can reset automatically on property changes.
 * See docs/design-docs/property-show-caching.md for full details.
 *
 * ## Cache bypass
 * To disable caching entirely, import and call `getPropertyDataInBatches`
 * directly (from `../helpers/dataBatching`) instead of going through
 * `getPropertyDataFromCache`. The cache layer internally calls
 * `getPropertyDataInBatches` on a miss, so bypassing the cache requires
 * no other code changes.
 *
 * Loading state is managed externally via the stateDispatchFunc reducer.
 * @param {object} params - Hook parameters
 * @param {string} params.propertyId - The property UUID to fetch data for
 * @param {object} params.dateSpan - Object with start and finish Luxon DateTime objects
 * @param {string} params.timezone - Timezone of the property
 * @param {object} params.initialData - Static/initial property data
 * @param {string} params.historianAggregation - Aggregation level
 * for historian data (e.g., 'PT30M', 'P1D')
 * @param {string} params.tradeAggregation - Aggregation level for trade data
 * @param {Function} [params.stateDispatchFunc] - Dispatch function for loading state reducer
 * @returns {object} Object containing:
 *   - combinedData: {object} Combined property data from GraphQL responses
 *   - cancelFunc: {Function} Cancels the in-flight fetch
 *   - clearError: {Function} Clears the error state without retrying
 *   - error: {Error|null} The last fetch error
 *   - handleRetry: {Function} Replays the failed query and resynchronises loading state
 *   - isRetrying: {boolean} True while a replay subscription is in flight
 */
const usePropertyData = ({
  propertyId,
  dateSpan,
  timezone,
  initialData,
  historianAggregation,
  tradeAggregation,
  stateDispatchFunc,
}) => {
  const environment = useRelayEnvironment();

  const [combinedData, setCombinedData] = useState(null);
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [queryType, setQueryType] = useState(null);

  // Incremented by handleRetry to force the first effect to re-run after a
  // failure. Decoupling retry from `error.replay()` lets a retry replay the
  // entire batched pipeline (property + meters + rules + trade summary)
  // rather than a single failed sub-query whose payload would not match
  // the combinedData shape.
  const [retryNonce, setRetryNonce] = useState(0);

  // Track if we're currently fetching data to prevent parallel fetches
  const isFetchingData = useRef(false);

  // Per-request cancellation token. Each new request creates a fresh { current: false }
  // object and stores it here. cancelFunc sets the active token's .current to true.
  // Old in-flight requests keep their own token reference, so they stay cancelled even
  // when a newer request starts — preventing the race where resetting a shared boolean
  // would "uncancel" a stale request.
  const cancelTokenRef = useRef({ current: false });

  const stateDispatchFuncRef = useRef(stateDispatchFunc);
  stateDispatchFuncRef.current = stateDispatchFunc;

  // Clear the module-level cache when the component unmounts so stale
  // data is not served when navigating back to the same property.
  useEffect(() => () => {
    initialiseCache(null);
  }, []);

  const handlePropertyDataError = useCallback((fetchError, message, failedQueryType) => {
    logger.error('Error fetching property data:', fetchError);
    setCombinedData(null);
    setIsRetrying(false);
    setError(fetchError);
    setQueryType(failedQueryType || null);
    if (stateDispatchFuncRef.current) {
      stateDispatchFuncRef.current({ type: PropertyShowLoadingActions.CANCEL_REQUEST });
    }
  }, []);

  // Retry replays the entire pipeline by bumping retryNonce, which is a dep of
  // the fetch effect. isRetrying clears in onDataFunc / handlePropertyDataError once the pipeline
  // resolves.
  const handleRetry = useCallback(() => {
    setError(null);
    setQueryType(null);
    setCombinedData(null);
    setIsRetrying(true);
    setRetryNonce((nonce) => nonce + 1);
  }, []);

  // Single effect: fetch property data whenever the property, date span, or
  // time-series parameters change, and on explicit retry.
  //
  // Merging the two former effects into one ensures that a dateSpan change
  // always cancels the in-flight token AND resets the parallel-fetch lock
  // (`isFetchingData`) before the new fetch starts — preventing the race
  // where a stale pipeline could complete after the user had already
  // navigated to a different date range.
  useEffect(() => {
    if (!propertyId || !dateSpan) {
      logger.error('No property ID or date span found for property data fetching');
      return undefined;
    }

    logger.log('Fetching property data for property:', propertyId);

    // Create a fresh cancellation token for this request. Old in-flight requests
    // retain their own (cancelled) token, so they cannot be "uncancelled".
    const cancelToken = { current: false };
    cancelTokenRef.current = cancelToken;

    // Delegate to the read-through cache. On a hit, onDataFunc fires
    // immediately with cached data. On a miss, onBeforeFetchFunc fires
    // first, then the fetch runs and onDataFunc fires with fresh data.
    getPropertyDataFromCache({
      propertyId,
      dateSpan,
      aggregation: historianAggregation,
      fetchParams: {
        propertyId,
        timezone,
        environment,
        tradeAggregation,
        initialData,
      },
      isFetchingRef: isFetchingData,
      cancelledRef: cancelToken,
      stateDispatchFunc,
      onBeforeFetchFunc: ({ segmentsExist }) => {
        if (cancelToken.current) return;
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.START_REQUEST });
          if (segmentsExist) {
            stateDispatchFunc({ type: PropertyShowLoadingActions.SET_PROPERTY_LOADED });
          }
        }
      },
      onDataFunc: (data, meters, rulesData, fromCache) => {
        if (cancelToken.current) return;
        setCombinedData(data);
        setIsRetrying(false);
        if (stateDispatchFunc) {
          if (fromCache) {
            const totalRulesCount = getTotalRulesCount(rulesData);
            const { batchSizeHours } = calculateTradeSetSummaryBatchSize({
              start: dateSpan.start,
              finish: dateSpan.finish,
              aggregation: historianAggregation,
              ruleCount: totalRulesCount,
            });
            const batchCount = generateBatchTimeRanges({
              start: dateSpan.start,
              finish: dateSpan.finish,
              batchSizeHours,
            }).length;
            stateDispatchFunc({
              type: PropertyShowLoadingActions.MARK_FULLY_LOADED,
              metersCount: meters.length,
              rulesCount: totalRulesCount,
              batchesLoaded: batchCount,
              batchesTotal: batchCount,
            });
          } else {
            stateDispatchFunc({ type: PropertyShowLoadingActions.MARK_FULLY_LOADED });
          }
        }
      },
      onErrorFunc: (fetchError, message, failedQueryType) => {
        if (cancelToken.current) return;
        handlePropertyDataError(fetchError, message, failedQueryType);
      },
    });

    return () => {
      cancelToken.current = true;
      // Reset the parallel-fetch lock immediately so the next effect run is
      // not blocked by the previous in-flight `isFetchingData.current` state
      // (which only clears in getPropertyDataInBatches' `finally` block).
      isFetchingData.current = false;
    };
  }, [
    handlePropertyDataError,
    propertyId,
    retryNonce,
    stateDispatchFunc,
    dateSpan?.start,
    dateSpan?.finish,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
  ]);

  const clearError = useCallback(() => {
    setError(null);
    setQueryType(null);
  }, []);

  const cancelFunc = useCallback(() => {
    cancelTokenRef.current.current = true;
    isFetchingData.current = false;
    logger.log('Data fetch cancelled by user');
  }, []);

  return {
    combinedData,
    cancelFunc,
    clearError,
    error,
    handleRetry,
    isRetrying,
    queryType,
  };
};

export default usePropertyData;
